import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import api, { fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Users, Truck, Package, MessageSquare, Tag, Check, X, Plus, ShieldCheck, Ban, Rocket } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");

  const reload = useCallback(async () => {
    const [s, se, co, dr, pr, inq, jb, cat] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users", { params: { role: "seller" } }),
      api.get("/admin/users", { params: { role: "company" } }),
      api.get("/admin/users", { params: { role: "driver" } }),
      api.get("/admin/products"),
      api.get("/admin/inquiries"),
      api.get("/admin/delivery-jobs"),
      api.get("/categories"),
    ]);
    setStats(s.data); setSellers(se.data); setCompanies(co.data); setDrivers(dr.data); setProducts(pr.data);
    setInquiries(inq.data); setJobs(jb.data); setCategories(cat.data);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const setUserStatus = async (user_id, status) => {
    await api.put(`/admin/users/${user_id}/status?status=${status}`);
    toast.success(`User ${status}`);
    reload();
  };
  const setFeatured = async (id, featured) => {
    await api.put(`/admin/transporters/${id}/feature?featured=${featured}`);
    toast.success(featured ? "Transporter featured" : "Removed from featured");
    reload();
  };
  const setDirectoryHidden = async (id, hidden) => {
    await api.put(`/admin/transporters/${id}/directory?hidden=${hidden}`);
    toast.success(hidden ? "Hidden from directory" : "Shown in directory");
    reload();
  };
  const setContactPublic = async (id, allowed) => {
    await api.put(`/admin/transporters/${id}/contact-public?allowed=${allowed}`);
    toast.success(allowed ? "Public contact allowed" : "Public contact disabled");
    reload();
  };
  const setProductStatus = async (pid, status) => {
    await api.put(`/admin/products/${pid}/status?status=${status}`);
    toast.success("Listing updated");
    reload();
  };
  const setProductVerified = async (pid, verified) => {
    await api.put(`/admin/products/${pid}/verify?verified=${verified}`);
    toast.success(verified ? "Marked WILTOM Verified" : "Verification removed");
    reload();
  };
  const removeProduct = async (pid) => {
    await api.delete(`/admin/products/${pid}`);
    toast.success("Listing removed");
    reload();
  };
  const removeJob = async (jid) => {
    await api.delete(`/admin/transport-jobs/${jid}`);
    toast.success("Transport job removed");
    reload();
  };
  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/categories", { name: newCat.trim() });
      toast.success("Category added");
      setNewCat("");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const statCards = stats ? [
    { label: "Pending sellers", value: stats.pending_sellers, icon: Users, alert: stats.pending_sellers > 0 },
    { label: "Pending drivers", value: stats.pending_drivers, icon: Truck, alert: stats.pending_drivers > 0 },
    { label: "Products", value: stats.total_products, icon: Package },
    { label: "Inquiries", value: stats.total_inquiries, icon: MessageSquare },
    { label: "Open jobs", value: stats.open_jobs, icon: Truck },
    { label: "Buyers", value: stats.total_buyers, icon: Users },
  ] : [];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="border-b border-foreground bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-accent" />
          <div>
            <span className="label-caps text-accent">Control room</span>
            <h1 className="font-display font-black text-3xl tracking-tight">Admin panel</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border mb-8">
          {statCards.map((c, i) => (
            <div key={i} className={`bg-white p-4 ${c.alert ? "ring-2 ring-inset ring-accent" : ""}`} data-testid={`stat-${i}`}>
              <c.icon className={`h-5 w-5 mb-2 ${c.alert ? "text-primary" : "text-muted-foreground"}`} />
              <div className="font-display font-black text-2xl">{c.value}</div>
              <div className="label-caps text-muted-foreground text-[10px]">{c.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="sellers">
          <TabsList className="rounded-none bg-muted border border-border flex-wrap h-auto">
            <TabsTrigger value="sellers" className="rounded-none" data-testid="admin-tab-sellers">Suppliers</TabsTrigger>
            <TabsTrigger value="companies" className="rounded-none" data-testid="admin-tab-companies">Companies</TabsTrigger>
            <TabsTrigger value="drivers" className="rounded-none" data-testid="admin-tab-drivers">Transporters</TabsTrigger>
            <TabsTrigger value="listings" className="rounded-none" data-testid="admin-tab-listings">Listings</TabsTrigger>
            <TabsTrigger value="inquiries" className="rounded-none" data-testid="admin-tab-inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="jobs" className="rounded-none" data-testid="admin-tab-jobs">Transport</TabsTrigger>
            <TabsTrigger value="categories" className="rounded-none" data-testid="admin-tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="checklist" className="rounded-none" data-testid="admin-tab-checklist">Launch Checklist</TabsTrigger>
          </TabsList>

          <TabsContent value="sellers" className="mt-6"><UserTable users={sellers} onStatus={setUserStatus} kind="seller" /></TabsContent>
          <TabsContent value="companies" className="mt-6"><UserTable users={companies} onStatus={setUserStatus} kind="company" /></TabsContent>
          <TabsContent value="drivers" className="mt-6"><UserTable users={drivers} onStatus={setUserStatus} kind="driver" onFeature={setFeatured} onHide={setDirectoryHidden} onContact={setContactPublic} /></TabsContent>

          <TabsContent value="listings" className="mt-6">
            {products.length === 0 ? <Empty text="No listings." /> : (
              <div className="border border-border divide-y divide-border">
                {products.map((p) => (
                  <div key={p.product_id} className="p-4 flex items-center gap-4" data-testid={`admin-product-${p.product_id}`}>
                    <img src={p.images?.[0] ? fileUrl(p.images[0]) : "https://images.pexels.com/photos/3806252/pexels-photo-3806252.jpeg"} alt="" className="h-14 w-14 object-cover border border-border" />
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">{p.title}{p.wiltom_verified && <Badge className="rounded-none bg-primary text-white text-[9px] uppercase tracking-wider" data-testid={`admin-verified-${p.product_id}`}>WILTOM Verified</Badge>}</div>
                      <div className="text-xs text-muted-foreground">{p.seller_name} · {p.category} · £{Number(p.price).toLocaleString()}</div>
                    </div>
                    <Badge className={`rounded-none text-[10px] uppercase ${p.status === "active" ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>{p.status}</Badge>
                    <Button size="sm" variant="outline" className="rounded-none" onClick={() => setProductVerified(p.product_id, !p.wiltom_verified)} data-testid={`verify-product-${p.product_id}`}>
                      {p.wiltom_verified ? "Unverify" : "Mark Verified"}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none" onClick={() => setProductStatus(p.product_id, p.status === "active" ? "inactive" : "active")} data-testid={`toggle-product-${p.product_id}`}>
                      {p.status === "active" ? "Disable" : "Enable"}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white" onClick={() => removeProduct(p.product_id)} data-testid={`remove-product-${p.product_id}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            {inquiries.length === 0 ? <Empty text="No inquiries." /> : (
              <div className="border border-border divide-y divide-border">
                {inquiries.map((inq) => (
                  <div key={inq.inquiry_id} className="p-4" data-testid={`admin-inquiry-${inq.inquiry_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{inq.product_title}</span>
                      <Badge className="rounded-none text-[10px] uppercase">{inq.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Buyer {inq.buyer_name} ({inq.buyer_email}) ↔ Seller {inq.seller_name || "—"}</p>
                    <div className="mt-3 space-y-2 border-l-2 border-border pl-3">
                      {(inq.messages && inq.messages.length ? inq.messages : [{ sender_role: "buyer", sender_name: inq.buyer_name, message: inq.message }]).map((m, i) => (
                        <div key={i} className="text-sm">
                          <span className={`text-[10px] uppercase tracking-wider mr-2 ${m.sender_role === "seller" ? "text-primary" : "text-muted-foreground"}`}>
                            {m.sender_role === "seller" ? "Seller" : "Buyer"}
                          </span>
                          {m.message}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="jobs" className="mt-6">
            {jobs.length === 0 ? <Empty text="No transport jobs." /> : (
              <div className="border border-border divide-y divide-border">
                {jobs.map((j) => (
                  <div key={j.job_id} className="p-4" data-testid={`admin-job-${j.job_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{j.title}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={`rounded-none text-[10px] uppercase ${j.status === "open" ? "bg-primary text-white" : j.status === "assigned" ? "bg-accent text-foreground" : "bg-muted"}`}>{j.status}</Badge>
                        <Button size="sm" variant="outline" className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white" onClick={() => removeJob(j.job_id)} data-testid={`remove-job-${j.job_id}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{j.pickup_location} → {j.delivery_location}{j.truck_type ? ` · ${j.truck_type}` : ""} · by {j.poster_name}{j.driver_name ? ` · transporter: ${j.driver_name}` : ""}</p>
                    <p className="text-xs text-muted-foreground mt-1">{j.applications_count || 0} applicant(s){j.job_category ? ` · ${j.job_category}` : ""}{j.request_quote ? " · quote requested" : j.price ? ` · £${j.price}` : ""}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="categories" className="mt-6">
            <div className="flex gap-2 mb-4 max-w-md">
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" className="rounded-none h-10" data-testid="new-category-input" />
              <Button onClick={addCategory} className="rounded-none bg-primary hover:bg-primary/90" data-testid="add-category-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <span key={c.category_id} className="inline-flex items-center gap-2 border border-border px-3 py-1.5 text-sm" data-testid={`category-${c.name}`}>
                  <Tag className="h-3.5 w-3.5 text-primary" /> {c.name}
                </span>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="checklist" className="mt-6">
            <LaunchReadiness />
            <LaunchChecklist stats={stats} drivers={drivers} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function UserTable({ users, onStatus, kind, onFeature, onHide, onContact }) {
  if (users.length === 0) return <Empty text={`No ${kind}s registered.`} />;
  return (
    <div className="border border-border divide-y divide-border">
      {users.map((u) => {
        const docs = u.documents || {};
        const hasDocs = docs.vehicle_photo || docs.insurance || docs.registration;
        return (
        <div key={u.user_id} className="p-4" data-testid={`admin-${kind}-${u.user_id}`}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <div className="font-medium">{u.company_name || u.name}</div>
              <div className="text-xs text-muted-foreground">{u.name} · {u.email}{u.phone ? ` · ${u.phone}` : ""}{u.whatsapp ? ` · WA ${u.whatsapp}` : ""}</div>
              {kind === "driver" && (
                <div className="text-xs text-muted-foreground mt-0.5">{u.vehicle_type || "—"}{u.service_areas ? ` · areas: ${u.service_areas}` : ""}{u.routes ? ` · ${u.routes}` : ""}</div>
              )}
            </div>
            <Badge className={`rounded-none text-[10px] uppercase ${u.status === "approved" ? "bg-primary text-white" : u.status === "rejected" || u.status === "suspended" ? "bg-destructive text-white" : u.status === "blocked" ? "bg-foreground text-white" : "bg-accent text-foreground"}`}>{u.status}</Badge>
            <div className="flex gap-2 flex-wrap">
              {u.status !== "approved" && (
                <Button size="sm" className="rounded-none bg-primary hover:bg-primary/90" onClick={() => onStatus(u.user_id, "approved")} data-testid={`approve-${u.user_id}`}>
                  <Check className="h-4 w-4 mr-1" /> Approve
                </Button>
              )}
              {u.status !== "rejected" && (
                <Button size="sm" variant="outline" className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white" onClick={() => onStatus(u.user_id, "rejected")} data-testid={`reject-${u.user_id}`}>
                  <X className="h-4 w-4 mr-1" /> Reject
                </Button>
              )}
              {u.status === "blocked" ? (
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => onStatus(u.user_id, "approved")} data-testid={`unblock-${u.user_id}`}>Unblock</Button>
              ) : (
                <Button size="sm" variant="outline" className="rounded-none border-foreground" onClick={() => onStatus(u.user_id, "blocked")} data-testid={`block-${u.user_id}`}>
                  <Ban className="h-4 w-4 mr-1" /> Block
                </Button>
              )}
            </div>
          </div>
          {kind === "driver" && (
            <div className="mt-2 flex flex-wrap gap-3 text-xs">
              <span className="label-caps text-muted-foreground">Docs:</span>
              {hasDocs ? (
                <>
                  {docs.vehicle_photo && <a href={fileUrl(docs.vehicle_photo)} target="_blank" rel="noopener noreferrer" className="text-primary underline" data-testid={`doc-vehicle-${u.user_id}`}>vehicle photo</a>}
                  {docs.insurance && <a href={fileUrl(docs.insurance)} target="_blank" rel="noopener noreferrer" className="text-primary underline" data-testid={`doc-insurance-${u.user_id}`}>insurance</a>}
                  {docs.registration && <a href={fileUrl(docs.registration)} target="_blank" rel="noopener noreferrer" className="text-primary underline" data-testid={`doc-registration-${u.user_id}`}>registration</a>}
                </>
              ) : <span className="text-destructive">No documents uploaded yet</span>}
            </div>
          )}
          {kind === "driver" && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3" data-testid={`directory-controls-${u.user_id}`}>
              <span className="label-caps text-muted-foreground mr-1">Directory:</span>
              {u.featured && <Badge className="rounded-none bg-accent text-foreground text-[9px] uppercase">Featured</Badge>}
              {u.directory_hidden && <Badge className="rounded-none bg-foreground text-white text-[9px] uppercase">Hidden</Badge>}
              {u.public_profile === false && <Badge className="rounded-none bg-muted text-[9px] uppercase">Profile off</Badge>}
              {u.contact_public && <Badge className="rounded-none bg-primary text-white text-[9px] uppercase">Public contact</Badge>}
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => onFeature(u.user_id, !u.featured)} data-testid={`feature-${u.user_id}`}>
                {u.featured ? "Unfeature" : "Feature"}
              </Button>
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => onHide(u.user_id, !u.directory_hidden)} data-testid={`hide-${u.user_id}`}>
                {u.directory_hidden ? "Show in directory" : "Hide from directory"}
              </Button>
              <Button size="sm" variant="outline" className="rounded-none" onClick={() => onContact(u.user_id, !u.contact_public)} data-testid={`contact-public-${u.user_id}`}>
                {u.contact_public ? "Disable public contact" : "Allow public contact"}
              </Button>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

function LaunchReadiness() {
  const [data, setData] = useState(null);
  const [legal, setLegal] = useState({ legal_business_address: "", legal_business_email: "", legal_ust_id: "" });
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/launch-readiness");
    setData(data);
    setLegal({
      legal_business_address: data.settings.legal_business_address || "",
      legal_business_email: data.settings.legal_business_email || "",
      legal_ust_id: data.settings.legal_ust_id || "",
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, value) => {
    await api.put("/admin/settings", { [key]: value });
    toast.success("Setting updated");
    load();
  };
  const saveLegal = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", legal); toast.success("Legal details saved"); load(); }
    finally { setBusy(false); }
  };
  const goLive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/go-live", { confirmation: confirm });
      toast.success(data.message);
      setConfirm("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Go Live failed");
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const s = data.settings;
  const toggles = [
    { key: "public_registration_enabled", label: "Public registration", on: s.public_registration_enabled },
    { key: "private_beta", label: "Beta banner", on: s.private_beta },
    { key: "transporter_directory_enabled", label: "Public transporter directory", on: s.transporter_directory_enabled },
    { key: "marketplace_enabled", label: "Marketplace public listings", on: s.marketplace_enabled },
    { key: "transport_jobs_enabled", label: "Transport job posting", on: s.transport_jobs_enabled },
  ];

  return (
    <div className="max-w-4xl space-y-6 mb-8" data-testid="launch-readiness">
      <div className="border-2 border-foreground bg-foreground text-white p-5">
        <div className="flex items-center gap-2">
          <Rocket className="h-5 w-5 text-accent" />
          <h3 className="font-display font-bold text-lg">Launch readiness / Go Live</h3>
          <Badge className={`rounded-none ml-auto uppercase text-[10px] tracking-wider ${data.launched ? "bg-primary text-white" : "bg-accent text-foreground"}`} data-testid="launch-status-badge">
            {data.launched ? "Live" : "Pre-launch / Private beta"}
          </Badge>
        </div>
        <p className="text-sm text-white/60 mt-1">Safety tool — review readiness, manage launch controls, then confirm Go Live. Legal details are never auto-filled.</p>
      </div>

      {/* Readiness checklist */}
      <div className="border border-border" data-testid="readiness-checklist">
        {data.checklist.map((c) => (
          <div key={c.key} className="p-3 flex items-center gap-3 border-b border-border last:border-b-0" data-testid={`readiness-${c.key}`}>
            <span className={`h-5 w-5 flex items-center justify-center shrink-0 ${c.ok ? "bg-primary text-white" : "bg-destructive text-white"}`}>
              {c.ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
            </span>
            <span className={`text-sm ${c.ok ? "" : "font-medium"}`}>{c.label}</span>
            {c.blocking && !c.ok && <Badge className="rounded-none ml-auto bg-destructive text-white text-[9px] uppercase">Blocking</Badge>}
          </div>
        ))}
      </div>

      {/* Legal details (manual) */}
      <div className="border border-border p-5">
        <h4 className="font-display font-bold">Legal details (fill manually from your Gewerbeschein)</h4>
        <p className="text-xs text-muted-foreground mt-1">These populate the Impressum, Datenschutz and Terms. Leave blank until your registration is confirmed — placeholders and the launch warning stay until both address and email are filled.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div><Label className="label-caps">Business address</Label><Input value={legal.legal_business_address} onChange={(e) => setLegal({ ...legal, legal_business_address: e.target.value })} className="rounded-none mt-1 h-10" data-testid="legal-address" /></div>
          <div><Label className="label-caps">Official business email</Label><Input value={legal.legal_business_email} onChange={(e) => setLegal({ ...legal, legal_business_email: e.target.value })} className="rounded-none mt-1 h-10" data-testid="legal-email" /></div>
          <div><Label className="label-caps">USt-IdNr (only if provided)</Label><Input value={legal.legal_ust_id} onChange={(e) => setLegal({ ...legal, legal_ust_id: e.target.value })} className="rounded-none mt-1 h-10" data-testid="legal-ust" /></div>
        </div>
        <Button onClick={saveLegal} disabled={busy} className="rounded-none bg-primary hover:bg-primary/90 mt-4" data-testid="save-legal-btn">Save legal details</Button>
      </div>

      {/* Manual launch controls */}
      <div className="border border-border p-5">
        <h4 className="font-display font-bold">Manual launch controls</h4>
        <div className="mt-3 divide-y divide-border">
          {toggles.map((t) => (
            <div key={t.key} className="py-3 flex items-center justify-between gap-4" data-testid={`control-${t.key}`}>
              <span className="text-sm font-medium">{t.label}</span>
              <Switch checked={t.on} onCheckedChange={(v) => toggle(t.key, v)} data-testid={`toggle-${t.key}`} />
            </div>
          ))}
          <div className="py-3 flex items-center justify-between gap-4 text-muted-foreground">
            <span className="text-sm">Payments / commission</span>
            <Badge className="rounded-none bg-muted text-[10px] uppercase">Inactive</Badge>
          </div>
          <div className="py-3 flex items-center justify-between gap-4 text-muted-foreground">
            <span className="text-sm">Resend email notifications</span>
            <Badge className="rounded-none bg-muted text-[10px] uppercase">Inactive</Badge>
          </div>
        </div>
      </div>

      {/* Go Live */}
      <div className={`border-2 p-5 ${data.can_go_live ? "border-primary" : "border-destructive/40 bg-destructive/5"}`} data-testid="go-live-box">
        <h4 className="font-display font-bold flex items-center gap-2"><Rocket className="h-4 w-4" /> Activate public launch</h4>
        {data.legal_placeholders_present ? (
          <p className="text-sm text-destructive mt-2" data-testid="go-live-blocked">Go Live blocked: legal placeholders must be completed before public launch.</p>
        ) : !data.can_go_live ? (
          <p className="text-sm text-destructive mt-2" data-testid="go-live-blocked">Go Live blocked: complete all required readiness items before public launch.</p>
        ) : (
          <p className="text-sm text-muted-foreground mt-2">All required checks passed. Type <strong>GO LIVE</strong> to activate public registration and remove the beta banner.</p>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type GO LIVE to confirm" disabled={!data.can_go_live}
                 className="rounded-none h-10 max-w-xs" data-testid="go-live-input" />
          <Button onClick={goLive} disabled={busy || !data.can_go_live || confirm.trim() !== "GO LIVE"}
                  className="rounded-none bg-primary hover:bg-primary/90 h-10" data-testid="go-live-btn">Go Live</Button>
        </div>
      </div>
    </div>
  );
}

function LaunchChecklist({ stats, drivers }) {
  const featuredCount = drivers.filter((d) => d.featured).length;
  const items = [
    { label: "Approve suppliers", done: stats && stats.pending_sellers === 0, note: stats ? `${stats.pending_sellers} pending` : "" },
    { label: "Approve companies", done: true, note: "Review under Companies tab" },
    { label: "Approve transporters", done: stats && stats.pending_drivers === 0, note: stats ? `${stats.pending_drivers} pending` : "" },
    { label: "Review transporter documents", done: false, note: "Open each transporter and verify vehicle photo, insurance & registration" },
    { label: "Feature trusted transporters", done: featuredCount > 0, note: `${featuredCount} featured` },
    { label: "Hide / block bad or fake users", done: false, note: "Use Block and Hide-from-directory controls" },
    { label: "Remove fake listings or jobs", done: false, note: "Use Listings and Transport tabs" },
    { label: "Fill legal pages (Impressum, Datenschutz, Terms)", done: false, note: "Replace all [ ] placeholders with real company details before launch" },
  ];
  return (
    <div className="max-w-3xl space-y-4" data-testid="launch-checklist">
      <div className="border border-accent bg-accent/10 p-4">
        <p className="font-display font-bold text-lg">Pre-launch checklist</p>
        <p className="text-sm text-muted-foreground mt-1">Work through these before opening WILTOM TradeLink to the public. Items marked done are auto-detected; the rest need manual review.</p>
      </div>
      <div className="border border-border divide-y divide-border">
        {items.map((it, i) => (
          <div key={i} className="p-4 flex items-start gap-3" data-testid={`checklist-item-${i}`}>
            <span className={`mt-0.5 h-5 w-5 flex items-center justify-center shrink-0 ${it.done ? "bg-primary text-white" : "border border-foreground text-transparent"}`}>
              <Check className="h-3.5 w-3.5" />
            </span>
            <div>
              <p className={`font-medium ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.label}</p>
              {it.note && <p className="text-xs text-muted-foreground mt-0.5">{it.note}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="empty-state">{text}</div>;
}
